/**
 * The chip rows Squares uses for presets, axes, checkpoints and the Results
 * filter. One look for single and multi select: a pill inside a 44px tap
 * target, the selected pill solid brand with a check, so the state never rests
 * on a tint alone. Single select is announced as a radio group, multi select
 * as toggle buttons.
 */
import { cn } from "@sportsmet/ui";
import type { KeyboardEvent, ReactNode } from "react";

export type typeSquaresChipOption = {
  value: string;
  label: string;
  disabled?: boolean | undefined;
};

type typeCommonProps = {
  /** Names the group for screen readers. */
  label: string;
  options: readonly typeSquaresChipOption[];
  testId?: string | undefined;
  className?: string | undefined;
};

export type typeSquaresChipGroupProps = typeCommonProps &
  (
    | {
        mode: "single";
        value: string | null;
        onChange: (value: string) => void;
      }
    | {
        mode: "multi";
        value: readonly string[];
        onChange: (value: readonly string[]) => void;
        /** Values that stay on and cannot be turned off (Final). */
        lockedValues?: readonly string[] | undefined;
      }
  );

const CheckIcon = (): ReactNode => (
  <svg
    className="h-icon-sm w-icon-sm"
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="m3.5 8.5 3 3 6-7"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export function squaresChipPillClass(input: {
  isSelected: boolean;
  isDisabled: boolean;
}): string {
  return cn(
    "inline-flex h-chip-height min-w-touch-target items-center justify-center gap-1 rounded-full border px-3 text-caption",
    "transition-colors duration-200 motion-reduce:transition-none",
    "group-focus-visible:outline-2 group-focus-visible:outline-offset-2 group-focus-visible:outline-focus-ring",
    input.isDisabled
      ? "border-disabled-border bg-disabled-bg text-disabled-text"
      : input.isSelected
        ? "border-primary-solid bg-primary-solid text-on-primary"
        : "border-line bg-surface text-ink group-hover:bg-surface-sunken",
  );
}

const CHIP_BUTTON_CLASS =
  "group flex min-h-touch-target min-w-touch-target shrink-0 items-center justify-center focus-visible:outline-none disabled:cursor-not-allowed";

function moveRadioFocus(event: KeyboardEvent<HTMLDivElement>): void {
  const keys = ["ArrowRight", "ArrowLeft", "ArrowDown", "ArrowUp"];
  if (!keys.includes(event.key)) return;
  const radios = [
    ...event.currentTarget.querySelectorAll<HTMLButtonElement>(
      '[role="radio"]:not([disabled])',
    ),
  ];
  if (radios.length === 0) return;
  const current = radios.indexOf(document.activeElement as HTMLButtonElement);
  const forward = event.key === "ArrowRight" || event.key === "ArrowDown";
  const next = forward
    ? (current + 1) % radios.length
    : (current - 1 + radios.length) % radios.length;
  event.preventDefault();
  radios[next]?.focus();
  radios[next]?.click();
}

export const SquaresChipGroup = (
  props: typeSquaresChipGroupProps,
): ReactNode => {
  const wrapClass = cn("flex flex-wrap items-center gap-2", props.className);

  if (props.mode === "single") {
    const hasSelection = props.options.some(
      (option) => option.value === props.value,
    );
    return (
      <div
        role="radiogroup"
        aria-label={props.label}
        data-testid={props.testId}
        className={wrapClass}
        onKeyDown={moveRadioFocus}
      >
        {props.options.map((option, index) => {
          const isSelected = option.value === props.value;
          const isDisabled = option.disabled ?? false;
          const isTabStop = hasSelection ? isSelected : index === 0;
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={isSelected}
              disabled={isDisabled}
              tabIndex={isTabStop ? 0 : -1}
              data-value={option.value}
              className={CHIP_BUTTON_CLASS}
              onClick={() => {
                if (!isSelected) props.onChange(option.value);
              }}
            >
              <span
                className={squaresChipPillClass({ isSelected, isDisabled })}
              >
                {isSelected ? <CheckIcon /> : null}
                {option.label}
              </span>
            </button>
          );
        })}
      </div>
    );
  }

  const locked = new Set(props.lockedValues ?? []);
  const selected = new Set(props.value);
  return (
    <div
      role="group"
      aria-label={props.label}
      data-testid={props.testId}
      className={wrapClass}
    >
      {props.options.map((option) => {
        const isSelected =
          selected.has(option.value) || locked.has(option.value);
        const isLocked = locked.has(option.value);
        const isDisabled = (option.disabled ?? false) || isLocked;
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={isSelected}
            disabled={isDisabled}
            data-value={option.value}
            className={CHIP_BUTTON_CLASS}
            onClick={() => {
              const next = isSelected
                ? props.value.filter((value) => value !== option.value)
                : [...props.value, option.value];
              props.onChange(next);
            }}
          >
            <span
              className={squaresChipPillClass({
                isSelected,
                isDisabled: (option.disabled ?? false) && !isLocked,
              })}
            >
              {isSelected ? <CheckIcon /> : null}
              {option.label}
            </span>
          </button>
        );
      })}
    </div>
  );
};
